// src/modules/admin/ReviewerList.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useLocation } from 'react-router-dom';
import * as XLSX from 'xlsx';

const ReviewerList = () => {
  const [reviewers, setReviewers] = useState([]);
  const [filteredReviewers, setFilteredReviewers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const location = useLocation();
  const defaultFilter = location.state?.defaultFilter || 'all';
  const [filter, setFilter] = useState(defaultFilter);
  const [page, setPage] = useState(1);
  const API = process.env.REACT_APP_API_BASE_URL;
  const PER_PAGE = 5;
  const platformId = localStorage.getItem('platformID');

  const [approvedCount, setApprovedCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);

  // Fetch reviewers
  useEffect(() => {
    const fetchReviewers = async () => {
      try {
        const response = await axios.get(`${API}/api/reviewer/reviewers`, {
          params: {
            status: filter === 'all' ? '' : filter,
            platformId,
          },
        });
        const data = response.data || [];
        setReviewers(data);
        setApprovedCount(data.filter(r => r.IsApproved === 1).length);
        setPendingCount(data.filter(r => r.IsApproved === 2).length);
        setRejectedCount(data.filter(r => r.IsApproved === 3).length);
        setPage(1);
      } catch (err) {
        console.error(err);
      }
    };
    fetchReviewers();
  }, [filter, API, platformId]);

  // Filter on search
  useEffect(() => {
    const term = searchTerm.toLowerCase();
    setFilteredReviewers(
      reviewers.filter((r) =>
        r.ReviewerName.toLowerCase().includes(term) || r.EmailID.toLowerCase().includes(term)
      )
    );
  }, [searchTerm, reviewers]);

  const paginatedReviewers = filteredReviewers.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const getStatusLabel = (status) => {
    switch (status) {
      case 1: return 'Approved';
      case 2: return 'Pending';
      case 3: return 'Rejected';
      default: return 'Not Approved';
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Reviewers List', 14, 20);

    const tableColumn = ['Reviewer Name', 'Email', 'Status'];
    const tableRows = filteredReviewers.map((r) => [
      r.ReviewerName,
      r.EmailID,
      getStatusLabel(r.IsApproved),
    ]);

    autoTable(doc, {
      startY: 30,
      head: [tableColumn],
      body: tableRows,
    });

    doc.save('reviewers_list.pdf');
  };

  const exportToExcel = () => {
    const data = filteredReviewers.map((r) => ({
      'Reviewer Name': r.ReviewerName,
      Email: r.EmailID,
      Status: getStatusLabel(r.IsApproved),
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Reviewers');
    XLSX.writeFile(workbook, 'reviewers_list.xlsx');
  };

  return (
    <div className="container mt-4 ml-2">
      <h3 className="page-title mb-4">Reviewers List</h3>

      

      {/* Search */}
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search by Reviewer Name or Email"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Filter Options */}
      <div className="mb-3 d-flex flex-wrap gap-3">
        {['all', 'approved', 'notapproved', 'pending', 'rejected'].map((status) => (
          <label key={status} className="me-3">
            <input
              type="radio"
              name="status"
              value={status}
              checked={filter === status}
              onChange={() => setFilter(status)}
            />{' '}
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </label>
        ))}
      </div>

      {/* Table */}
      <table className="table table-bordered">
        <thead>
          <tr>
            <th>Reviewer Name</th>
            <th>Email</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {paginatedReviewers.length > 0 ? (
            paginatedReviewers.map((r) => (
              <tr key={r.ReviewerID}>
                <td>{r.ReviewerName}</td>
                <td>{r.EmailID}</td>
                <td>{getStatusLabel(r.IsApproved)}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="3" className="text-center">No reviewers found.</td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="d-flex justify-content-between mt-3">
        <button
          className="btn btn-secondary"
          onClick={() => setPage(Math.max(page - 1, 1))}
          disabled={page === 1}
        >
          Previous
        </button>
        <span>Page {page}</span>
        <button
          className="btn btn-secondary"
          onClick={() => setPage(page + 1)}
          disabled={page * PER_PAGE >= filteredReviewers.length}
        >
          Next
        </button>
      </div>

      {/* Export Buttons */}
      {filteredReviewers.length > 0 && (
        <div className="mt-3">
          <button className="btn btn-success me-2" onClick={exportToPDF}>
            Export to PDF
          </button>
          <button className="btn btn-outline-primary" onClick={exportToExcel}>
            Export to Excel
          </button>
        </div>
      )}
    </div>
  );
};

export default ReviewerList;
